import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileDown, Pencil, Trash2, MessageCircle, Layers, Upload, FileText, X, FileSpreadsheet, ArrowUpDown, FileInput } from "lucide-react";
import { toast } from "sonner";
import { API, backendUrl, publicAppUrl, inr, fmtDate, waLink, downloadExcel } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination } from "@/components/Pagination";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600",
  sent: "bg-blue-100 text-blue-700",
  accepted: "bg-emerald-100 text-emerald-700",
  paid: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
};

const emptyPage = { id: "", title: "", category: "", content: "", pdf_name: "", pdf_path: "" };

function buildWaMessage(doc) {
  const kind = doc.type === "quotation" ? "Quotation" : "Invoice";
  const lines = [
    `Hello ${doc.customer?.name || ""},`,
    ``,
    `Thank you for your enquiry. Please find your ${kind} ${doc.number}:`,
    ...doc.items.map((i) => `• ${i.name} × ${i.qty} — ₹${(i.qty * i.rate).toLocaleString("en-IN")}`),
    ``,
    `Subtotal: ₹${doc.subtotal?.toLocaleString("en-IN")}`,
    `GST (${doc.tax_pct}%): ₹${doc.tax_amount?.toLocaleString("en-IN")}`,
    `Grand Total: ₹${doc.total?.toLocaleString("en-IN")}`,
  ];
  if (doc.payment_terms) lines.push(`Payment Terms: ${doc.payment_terms}`);
  lines.push(``, `📄 *Show PDF*`, `${publicAppUrl}/api/documents/${doc.id}/pdf`);
  return lines.join("\n");
}

export default function Documents({ fixedType = "" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageDocs = ["admin", "accountant"].includes(user?.role);
  const [type, setType] = useState(fixedType || "quotation");
  const [docs, setDocs] = useState([]);
  const [pagesOpen, setPagesOpen] = useState(false);
  const [staticPages, setStaticPages] = useState([]);
  const [pageForm, setPageForm] = useState(emptyPage);
  const [spFile, setSpFile] = useState(null);
  const [spSaving, setSpSaving] = useState(false);
  const [sort, setSort] = useState({ key: "", dir: "desc" });
  const [pg, setPg] = useState({ page: 1, size: 10 });
  const spFileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await API.get("/documents", { params: { type } });
    setDocs(data);
  }, [type]);

  const loadPages = useCallback(async () => {
    const { data } = await API.get("/static-pages");
    setStaticPages(data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const setStatus = async (id, status) => {
    await API.patch(`/documents/${id}/status`, { status });
    load();
  };

  const remove = async (id) => {
    await API.delete(`/documents/${id}`);
    toast.success("Deleted");
    load();
  };

  const convertToInvoice = (d) => {
    navigate(`/documents/new?type=invoice&from=${d.id}`);
  };

  const savePage = async () => {
    if (!pageForm.title.trim()) return toast.error("Page title is required");
    setSpSaving(true);
    try {
      const payload = { title: pageForm.title.trim(), category: pageForm.category.trim() || "General", content: pageForm.content };
      let pid = pageForm.id;
      if (pid) {
        await API.patch(`/static-pages/${pid}`, payload);
      } else {
        const { data } = await API.post("/static-pages", payload);
        pid = data.id;
      }
      if (spFile && pid) {
        const fd = new FormData();
        fd.append("file", spFile);
        await API.post(`/static-pages/${pid}/pdf`, fd);
      }
      toast.success(pageForm.id ? "Static page updated" : "Static page created");
      setPageForm(emptyPage);
      setSpFile(null);
      if (spFileRef.current) spFileRef.current.value = "";
      loadPages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSpSaving(false);
    }
  };

  const removePagePdf = async () => {
    if (!pageForm.id) return;
    await API.delete(`/static-pages/${pageForm.id}/pdf`);
    setPageForm((p) => ({ ...p, pdf_name: "", pdf_path: "" }));
    toast.success("PDF removed from page");
    loadPages();
  };

  const deletePage = async (id) => {
    await API.delete(`/static-pages/${id}`);
    toast.success("Static page deleted");
    loadPages();
  };

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const [colFilters, setColFilters] = useState({ number: "", customer: "", status: "all" });
  const filtered = docs.filter((d) => {
    if (colFilters.number && !(d.number || "").toLowerCase().includes(colFilters.number.toLowerCase())) return false;
    if (colFilters.customer && !(d.customer?.name || "").toLowerCase().includes(colFilters.customer.toLowerCase()) && !(d.customer?.company || "").toLowerCase().includes(colFilters.customer.toLowerCase())) return false;
    if (colFilters.status !== "all") {
      const ps = d.type === "invoice" && d.due != null ? (d.due <= 0.009 ? "paid" : d.paid > 0 ? "partial" : "unpaid") : d.status;
      const want = colFilters.status === "cancelled" ? "rejected" : colFilters.status;
      if (ps !== want) return false;
    }
    return true;
  });
  const sorted = [...filtered].sort((a, b) => {
    if (!sort.key) return 0;
    const va = sort.key === "created_at" ? a.created_at || "" : sort.key === "total" ? a.total || 0 : a.number || "";
    const vb = sort.key === "created_at" ? b.created_at || "" : sort.key === "total" ? b.total || 0 : b.number || "";
    if (typeof va === "string") return sort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sort.dir === "asc" ? va - vb : vb - va;
  });
  const paged = sorted.slice((pg.page - 1) * pg.size, pg.page * pg.size);

  const SortHead = ({ label, k, className = "" }) => (
    <TableHead className={`cursor-pointer select-none ${className}`} onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
      {label} <ArrowUpDown className="inline h-3 w-3 text-slate-400" />
    </TableHead>
  );

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {!fixedType && (
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Performa &amp; Sale Invoices</h1>
            <p className="text-sm text-slate-500">Create branded PDFs and share them on WhatsApp</p>
          </div>
        )}
        <div className={`flex flex-wrap gap-2 ${fixedType ? "ml-auto" : ""}`}>
          <Button variant="outline" onClick={() => downloadExcel(`/documents/export?type=${type}`, `${type}s.xlsx`)} data-testid="documents-export-button">
            <FileSpreadsheet className="mr-1 h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => { setPagesOpen(true); loadPages(); }} data-testid="static-pages-button">
            <Layers className="mr-1 h-4 w-4" /> Static Pages
          </Button>
          {(type === "quotation" || canManageDocs) && (
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate(`/documents/new?type=${type}`)} data-testid="new-document-button">
              <Plus className="mr-1 h-4 w-4" /> {type === "invoice" ? "New Sale Invoice" : "New Performa Invoice"}
            </Button>
          )}
          {!fixedType && type === "invoice" && (
            <span className="self-center text-xs text-slate-400" data-testid="invoice-create-hint">Invoices are created from Accounting → Sales (admin/accountant)</span>
          )}
        </div>
      </div>

      {!fixedType && (
        <Tabs value={type} onValueChange={(v) => { setType(v); setPg((p) => ({ ...p, page: 1 })); }}>
          <TabsList data-testid="document-type-tabs">
            <TabsTrigger value="quotation" data-testid="tab-quotations">Performa Invoices</TabsTrigger>
            <TabsTrigger value="invoice" data-testid="tab-invoices">Sale Invoices</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      <div className="rounded-lg border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHead label="Number" k="number" />
              <TableHead>Customer</TableHead>
              <SortHead label="Date" k="created_at" />
              <SortHead label="Total" k="total" className="text-right" />
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
            <TableRow className="bg-slate-50/60" data-testid="documents-filter-row">
              <TableHead className="py-1"><Input value={colFilters.number} onChange={(e) => { setColFilters({ ...colFilters, number: e.target.value }); setPg((p) => ({ ...p, page: 1 })); }} placeholder="Filter..." className="h-7 text-xs" data-testid="filter-number" /></TableHead>
              <TableHead className="py-1"><Input value={colFilters.customer} onChange={(e) => { setColFilters({ ...colFilters, customer: e.target.value }); setPg((p) => ({ ...p, page: 1 })); }} placeholder="Filter..." className="h-7 text-xs" data-testid="filter-customer" /></TableHead>
              <TableHead className="py-1" />
              <TableHead className="py-1" />
              <TableHead className="py-1">
                <Select value={colFilters.status} onValueChange={(v) => { setColFilters({ ...colFilters, status: v }); setPg((p) => ({ ...p, page: 1 })); }}>
                  <SelectTrigger className="h-7 w-28 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["all", "unpaid", "partial", "paid", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All" : s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </TableHead>
              <TableHead className="py-1" />
            </TableRow>
          </TableHeader>
          <TableBody data-testid="documents-table">
            {paged.map((d) => (
              <TableRow key={d.id} data-testid={`document-row-${d.id}`}>
                <TableCell className="font-semibold text-indigo-700">{d.number}</TableCell>
                <TableCell>
                  <div className="font-medium">{d.customer?.name || "-"}</div>
                  <div className="text-xs text-slate-500">{d.customer?.company}</div>
                </TableCell>
                <TableCell>{fmtDate(d.created_at)}</TableCell>
                <TableCell className="text-right font-semibold">
                  {inr(d.total)}
                  {d.type === "invoice" && d.due != null && (
                    <div className={`text-[11px] font-medium ${d.due > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={`doc-due-${d.id}`}>{d.due > 0 ? `Due ${inr(d.due)}` : "Paid"}</div>
                  )}
                </TableCell>
                <TableCell>
                  {d.type === "invoice" ? (
                    (() => {
                      const ps = d.due != null ? (d.due <= 0.009 ? "Paid" : d.paid > 0 ? "Partial" : "Unpaid") : d.status;
                      const cls = ps === "Paid" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ps === "Partial" ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100";
                      return <Badge className={cls} data-testid={`doc-paystatus-${d.id}`}>{ps}</Badge>;
                    })()
                  ) : (
                    <Select value={d.status} onValueChange={(v) => setStatus(d.id, v)}>
                      <SelectTrigger className={`h-8 w-28 text-xs font-medium capitalize ${STATUS_STYLE[d.status] || ""}`} data-testid={`doc-status-${d.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["sent", "rejected"].map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {d.attachment_url && (
                      <Button variant="ghost" size="icon" onClick={() => window.open(`${backendUrl}${d.attachment_url}`, "_blank")} data-testid={`doc-attachment-${d.id}`} aria-label="Attachment">
                        <FileText className="h-4 w-4 text-slate-500" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => window.open(`${backendUrl}/api/documents/${d.id}/pdf`, "_blank")} data-testid={`doc-pdf-${d.id}`}>
                      <FileDown className="mr-1 h-3.5 w-3.5" /> PDF
                    </Button>
                    {d.type === "quotation" && !d.converted_invoice_id && canManageDocs && (
                      <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => convertToInvoice(d)} data-testid={`doc-convert-${d.id}`}>
                        <FileInput className="mr-1 h-3.5 w-3.5" /> Invoice
                      </Button>
                    )}
                    {d.type === "quotation" && d.converted_invoice_id && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid={`doc-converted-${d.id}`}>
                        Invoiced{d.converted_invoice_number ? `: ${d.converted_invoice_number}` : ""}
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      className="bg-[#25D366] text-white hover:bg-[#1fb457]"
                      onClick={() => window.open(waLink(d.customer?.phone, buildWaMessage(d)), "_blank")}
                      disabled={!d.customer?.phone}
                      data-testid={`doc-whatsapp-${d.id}`}
                    >
                      <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp
                    </Button>
                    {((d.type === "invoice" && canManageDocs) || (d.type === "quotation" && !d.converted_invoice_id)) && (
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/documents/${d.id}/edit`)} data-testid={`doc-edit-${d.id}`} aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {(d.type === "quotation" || canManageDocs) && (
                      <Button variant="ghost" size="icon" onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`} aria-label="Delete">
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-slate-500">
                  <EmptyState compact title={`No ${type}s yet`} hint="Create your first one." />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination total={sorted.length} page={pg.page} size={pg.size} onChange={setPg} testid="documents-pagination" />
      </div>
      <Badge variant="outline" className="text-xs">Tip: PDFs open publicly — the WhatsApp message includes a direct PDF link for your client.</Badge>

      <Dialog open={pagesOpen} onOpenChange={setPagesOpen}>
        <DialogContent className="max-w-3xl" data-testid="static-pages-dialog">
          <DialogHeader>
            <DialogTitle>Static Pages for PDFs</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Upload ready-made PDF pages (company profile, certifications, bank details etc.) — they get merged into the quotation/invoice PDF before or after the generated content.</p>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="space-y-2" data-testid="static-pages-list">
              {staticPages.map((p) => (
                <div key={p.id} className="rounded-md border border-slate-200 p-3" data-testid={`static-page-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{p.title}</div>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-[10px]">{p.category || "General"}</Badge>
                        {p.pdf_name && (
                          <Badge className="bg-indigo-100 text-[10px] text-indigo-700 hover:bg-indigo-100" data-testid={`static-page-pdf-badge-${p.id}`}>
                            <FileText className="mr-1 h-2.5 w-2.5" /> {p.pdf_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setPageForm({ id: p.id, title: p.title, category: p.category || "", content: p.content || "", pdf_name: p.pdf_name || "", pdf_path: p.pdf_path || "" }); setSpFile(null); }} data-testid={`static-page-edit-${p.id}`} aria-label="Edit page">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deletePage(p.id)} data-testid={`static-page-delete-${p.id}`} aria-label="Delete page">
                        <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    </div>
                  </div>
                  {p.pdf_path ? (
                    <a href={`${backendUrl}/api/files/${p.pdf_path}`} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline" data-testid={`static-page-pdf-view-${p.id}`}>
                      <FileText className="h-3 w-3" /> View uploaded PDF
                    </a>
                  ) : (
                    <div className="mt-1.5 line-clamp-2 whitespace-pre-line text-xs text-slate-500">{p.content || "No content"}</div>
                  )}
                </div>
              ))}
              {staticPages.length === 0 && <div className="rounded-md border border-dashed border-slate-300 p-2"><EmptyState compact title="No static pages yet" hint="Create one on the right." /></div>}
            </div>
            <div className="space-y-3 rounded-md border border-slate-200 p-4">
              <div className="text-sm font-semibold text-slate-900">{pageForm.id ? "Edit Page" : "New Page"}</div>
              <div>
                <Label>Title *</Label>
                <Input value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} placeholder="e.g. Company Profile" className="mt-1.5" data-testid="static-page-title-input" />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={pageForm.category} onChange={(e) => setPageForm({ ...pageForm, category: e.target.value })} placeholder="e.g. Intro, Bank Details, Certifications" className="mt-1.5" data-testid="static-page-category-input" />
              </div>
              <div>
                <Label>PDF Page (recommended)</Label>
                {pageForm.pdf_name ? (
                  <div className="mt-1.5 flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid="static-page-pdf-chip">
                    <a href={`${backendUrl}/api/files/${pageForm.pdf_path}`} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-2 text-sm text-indigo-700 hover:underline">
                      <FileText className="h-4 w-4 shrink-0" />
                      <span className="truncate">{pageForm.pdf_name}</span>
                    </a>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={removePagePdf} data-testid="static-page-pdf-remove" aria-label="Remove PDF">
                      <X className="h-3.5 w-3.5 text-rose-500" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <input ref={spFileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => setSpFile(e.target.files[0] || null)} data-testid="static-page-pdf-input" />
                    <Button variant="outline" className="mt-1.5 w-full border-dashed" onClick={() => spFileRef.current?.click()} data-testid="static-page-pdf-upload-button">
                      <Upload className="mr-2 h-4 w-4" /> {spFile ? spFile.name : "Upload PDF Page"}
                    </Button>
                    <p className="mt-1 text-[11px] text-slate-400">The uploaded PDF is merged as full page(s) into the quote/invoice PDF.</p>
                  </>
                )}
              </div>
              {!pageForm.pdf_name && !spFile && (
                <div>
                  <Label>Or type text content</Label>
                  <Textarea rows={4} value={pageForm.content} onChange={(e) => setPageForm({ ...pageForm, content: e.target.value })} placeholder="Page text — each line becomes a paragraph in the PDF" className="mt-1.5" data-testid="static-page-content-input" />
                </div>
              )}
              <div className="flex gap-2">
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={savePage} disabled={spSaving} data-testid="save-static-page-button">{spSaving ? "Saving..." : pageForm.id ? "Update Page" : "Create Page"}</Button>
                {pageForm.id && (
                  <Button variant="outline" onClick={() => { setPageForm(emptyPage); setSpFile(null); }} data-testid="cancel-static-page-edit">Cancel</Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
